import React, { useRef, useState } from 'react'
import cn from 'classnames'
import api from 'api/api'
import { claimWelcomeGift, type LocalWalletStatus } from 'lib/localWallet'
import { readWalletBackupFile, verifyWalletBackupText } from 'lib/walletFileBackup'
import { formatSats, Reward, StepHeader } from './WalletSetup'
import styles from './walletSetup.module.scss'

// "Check your wallet file": an optional reward after signup, opened from the
// notification or the Wallet. Picking the saved file proves it opens the wallet;
// the keys never leave the device.
export default function WalletFileCheck({
    status,
    onStatus,
    onClose,
}: {
    status: LocalWalletStatus | null
    onStatus: (status: LocalWalletStatus) => void
    onClose: () => void
}) {
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [checked, setChecked] = useState(!!status?.backup_file_verified)
    // Only a check made here shows the reward; an earlier one was already paid.
    const [rewarded, setRewarded] = useState(false)
    const fileInput = useRef<HTMLInputElement>(null)
    const sats = status?.gift_step_sats?.file_check ?? 0

    const check = async (file: File | undefined) => {
        if (!file || busy) return
        setBusy(true)
        setError('')
        try {
            await verifyWalletBackupText(await readWalletBackupFile(file))
            // Show the result once the reward is claimed, so the screen does not flip.
            if (sats > 0) {
                onStatus(await claimWelcomeGift())
                setRewarded(true)
                void api.getBsvBalance().catch(() => {})
            }
            setChecked(true)
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : String(caught))
        } finally {
            setBusy(false)
        }
    }

    return (
        <div
            className={styles.overlay}
            role="dialog"
            aria-modal="true"
            aria-label="Check your wallet file"
        >
            <div className={cn(styles.screen, checked && styles.celebrate)}>
                <StepHeader
                    name={checked ? 'Wallet file checked' : 'Check wallet file'}
                    onLater={checked ? undefined : onClose}
                    laterLabel="Not now"
                />
                <div className={styles.body}>
                    {rewarded && <span className={styles.amount}>+{formatSats(sats)}</span>}
                    <h1 className={styles.title}>
                        {checked ? 'Your wallet file works' : 'Check your wallet file'}
                    </h1>
                    <p className={styles.text}>
                        {checked
                            ? rewarded
                                ? 'It opens your wallet. The sats are on their way.'
                                : 'It opens your wallet. Keep it somewhere safe.'
                            : 'Pick the file you saved. Your keys never leave this phone.'}
                    </p>
                </div>
                <div className={styles.actions}>
                    {error && (
                        <p className={styles.error} role="alert">
                            {error}
                        </p>
                    )}
                    <input
                        ref={fileInput}
                        type="file"
                        accept="application/json,.json"
                        className={styles.hiddenInput}
                        hidden
                        onChange={event => {
                            void check(event.target.files?.[0])
                            event.target.value = ''
                        }}
                    />
                    {checked ? (
                        <button type="button" className={styles.primary} onClick={onClose}>
                            Done
                        </button>
                    ) : (
                        <button
                            type="button"
                            className={styles.primary}
                            onClick={() => fileInput.current?.click()}
                            disabled={busy}
                            data-testid="wallet-file-check-choose"
                        >
                            {busy ? 'Checking…' : 'Choose wallet file'}
                        </button>
                    )}
                    {!checked && <Reward sats={sats} />}
                </div>
            </div>
        </div>
    )
}
